/**
 * The best-effort context refresh that follows a language switch.
 *
 * Its own module, and not part of `i18n.ts`, so it stays unit-testable: `i18n.ts` reaches the router
 * (and through it the whole route tree, and through that the monolith's SST resources), which no unit
 * test can import. Same reason `language-path.ts` is a separate file.
 */
import type { PortalLocale } from '@worldcoo/monolith/portals';

/**
 * Re-read the request context for `lang` and merge it into the router's, ignoring a failed fetch.
 *
 * WHY THE FAILURE IS IGNORED (TEC-3888). This call is pure catch-up: by the time it fires,
 * `changeLanguage` has already written the `language` cookie, set `context.locale` and applied
 * `updateRouterLocale`, all synchronously, and the next navigation recomputes the whole context
 * server-side anyway. So a refresh that never lands costs nothing the visitor can see — but left
 * unhandled its rejection reached the global `unhandledrejection` handler and filed a board ticket.
 * A person clicking four language buttons in half a second produced exactly that.
 *
 * Only the fetch is wrapped: a programming error thrown while merging is a real bug and must still
 * surface.
 *
 * WHY THE GUARD COMPARES `context.locale` TO `lang`. Responses can land out of order, and the older
 * one would otherwise win and drag the context back to a language the visitor has already left.
 * `changeLanguage` sets `context.locale = lang` synchronously before calling, and for an explicit
 * switch the handler always echoes the locale it was asked for — the server-fn URL carries no locale
 * prefix, so `parseRequest` resolves the locale from the `lang` argument. That makes "is
 * `context.locale` still `lang`?" exactly "is this still the language most recently asked for", with
 * no counter and no module state to keep.
 */
export async function refreshRouterContextForLocale<TContext extends { locale: PortalLocale }>(
	lang: PortalLocale,
	context: { locale: PortalLocale } | undefined,
	fetchContext: (lang: PortalLocale) => Promise<TContext>,
): Promise<void> {
	let fresh: TContext;
	try {
		fresh = await fetchContext(lang);
	} catch {
		// The switch itself already took effect; a refresh we could not fetch changes nothing.
		return;
	}

	if (!context) return;
	if (context.locale !== lang) return;

	Object.assign(context, fresh);
}
